import { useEffect, useId, useReducer, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Size, nextQueued, t, uploadQueueReducer, type QueuedFile, type UploadFn, type UploadStatus } from '@bms2/shared';
import { Button } from '../controls';
import { Icon } from '../Icon';
import styles from './shared.module.css';
import { removeLabel } from '../removeLabel';

/**
 * The file drop zone (piece 4, decision 0070): a native file input with drop
 * events added, never a replacement for it, so the keyboard path and the
 * system picker keep working. Several files queue at once; each has its own
 * native progress element, its own failure and its own retry that leaves the
 * others alone.
 *
 * 🔒 The browser's own control is hidden, never removed (decision 1328): the
 * input stays in the page, focusable and named by its label, and an outlined
 * Choose Files in the application's style is the label's own face. The whole
 * dashed area opens the picker, so a click anywhere in it is the same as the
 * button.
 */
export const dropZoneDocumentedStates = ['idle', 'uploading', 'failed'] as const;

export interface DropZoneProps {
  upload: UploadFn;
  /** A queue to open with, for the harness's states and a page that restores one. */
  initialQueue?: readonly QueuedFile[];
  /** Uploads run one at a time from the queue; the harness's static states turn the runner off. */
  autoStart?: boolean;
  /** Files handed in from a drop outside the zone, such as onto the tile that opened it; they queue as if dropped here. */
  initialFiles?: readonly File[];
  /**
   * 🔒 Stop (decision 2422): each rise of this count aborts the request in
   * flight and stops every file still queued; a file already landed stays Done.
   */
  stopCount?: number;
  /** The queue as it stands after each change, for the window that names what landed. */
  onQueueChange?: (queue: readonly QueuedFile[]) => void;
  /** 🔒 Decision 11301: fields of the caller's own drawn under one queued file's name, its name and date in the upload window. Absent, a row draws none. */
  rowFields?: (file: QueuedFile) => ReactNode;
  /** 🔒 Decision 11300 (row T3673): which waiting file the runner may start; absent, every one. A file this refuses waits for a later press. */
  mayStart?: (id: string) => boolean;
}

const statusKey: Record<UploadStatus, 'drop.status.queued' | 'drop.status.uploading' | 'drop.status.done' | 'drop.status.failed' | 'drop.status.stopped'> = {
  queued: 'drop.status.queued',
  uploading: 'drop.status.uploading',
  done: 'drop.status.done',
  failed: 'drop.status.failed',
  stopped: 'drop.status.stopped',
};

function progressLabel(name: string): string {
  return `${t('drop.progressOf')} ${name}`;
}

let sequence = 0;

export function DropZone({ upload, initialQueue = [], autoStart = true, initialFiles = [], stopCount = 0, onQueueChange, rowFields, mayStart }: DropZoneProps) {
  const inputId = useId();
  /**
   * The bytes behind each queued row, held beside the queue so the upload can
   * send them and the queue stays plain data. Files handed in at mount are
   * queued in the same step, once.
   */
  const [seed] = useState(() => {
    const held = new Map<string, Blob>();
    if (initialFiles.length === 0) return { blobs: held, rows: [] as { id: string; name: string; size: number }[] };
    sequence += 1;
    const rows = initialFiles.map((f, i) => ({ id: `upload-${sequence}-${i}`, name: f.name, size: f.size }));
    initialFiles.forEach((f, i) => held.set(rows[i]!.id, f));
    return { blobs: held, rows };
  });
  const blobs = useRef(seed.blobs);
  const [queue, dispatch] = useReducer(uploadQueueReducer, initialQueue, (q) => (seed.rows.length === 0 ? [...q] : uploadQueueReducer([...q], { type: 'add', files: seed.rows })));
  const [over, setOver] = useState(false);
  const running = useRef<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const stopsSeen = useRef(stopCount);

  useEffect(() => {
    onQueueChange?.(queue);
  }, [queue, onQueueChange]);

  // 🔒 Stop aborts the request in flight and stops the rest (decision 2422); the runner then finds nothing queued.
  useEffect(() => {
    if (stopCount === stopsSeen.current) return;
    stopsSeen.current = stopCount;
    inFlight.current?.abort();
    dispatch({ type: 'stop' });
  }, [stopCount]);

  /** A click on the dashed area's own ground opens the picker; one on a control inside it does that control's work. */
  const openFromArea = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a, input, label, [data-upload-queue]')) return;
    input.current?.click();
  };

  const enqueue = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    sequence += 1;
    const rows = Array.from(files).map((f, i) => ({ id: `upload-${sequence}-${i}`, name: f.name, size: f.size }));
    Array.from(files).forEach((f, i) => blobs.current.set(rows[i]!.id, f));
    dispatch({ type: 'add', files: rows });
  };

  useEffect(() => {
    if (!autoStart || running.current !== null) return;
    const next = mayStart ? queue.find((file) => file.status === 'queued' && mayStart(file.id)) : nextQueued(queue);
    if (!next) return;
    running.current = next.id;
    const controller = new AbortController();
    inFlight.current = controller;
    dispatch({ type: 'start', id: next.id });
    const blob = blobs.current.get(next.id);
    upload({ id: next.id, name: next.name, size: next.size, ...(blob ? { blob } : {}) }, (fraction) => dispatch({ type: 'progress', id: next.id, progress: fraction }), controller.signal).then(
      () => {
        running.current = null;
        inFlight.current = null;
        dispatch({ type: 'done', id: next.id });
      },
      /* silent: the failure is drawn on the file's own row as its reason. */ (error: unknown) => {
        running.current = null;
        inFlight.current = null;
        dispatch({ type: 'fail', id: next.id, error: error instanceof Error ? error.message : String(error) });
      },
    );
  }, [queue, upload, autoStart, mayStart]);

  return (
    <div
      className={styles.dropZone}
      data-drop-zone="true"
      data-over={over ? 'true' : 'false'}
      onClick={openFromArea}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        enqueue(e.dataTransfer.files);
      }}
    >
      <input
        ref={input}
        id={inputId}
        type="file"
        multiple
        className={styles.dropInput}
        onChange={(e) => {
          enqueue(e.target.files);
          e.target.value = '';
        }}
      />
      <label htmlFor={inputId} className={styles.dropLabel}>
        <Icon meaning="uploadSlot" size="header" />
        <span>{t('drop.prompt')}</span>{' '}
        {/* The space above keeps the label's name two words apart for a screen reader; the flex gap draws the visible one. */}
        <span className={styles.dropChoose} data-drop-choose="true">
          {t('drop.choose')}
        </span>
      </label>
      {queue.length > 0 ? (
        <ul className={styles.dropQueue} data-upload-queue="true">
          {queue.map((f) => (
            <li key={f.id} className={styles.dropRow} data-upload={f.status} data-upload-id={f.id}>
              <span className={styles.dropName}>{f.name}</span>
              <span className={styles.dropMeta}>
                <span className={styles.dropStatus}>{t(statusKey[f.status])}</span>
                {f.status === 'failed' ? <Button size={Size.Small} text={[t('drop.retry')]} actions={[() => dispatch({ type: 'retry', id: f.id })]} /> : null}
                <button type="button" className={styles.iconButton} aria-label={removeLabel('drop.remove', f.name)} onClick={() => dispatch({ type: 'remove', id: f.id })}>
                  <Icon meaning="remove" size="inline" />
                </button>
              </span>
              {rowFields ? <RowFields file={f} draw={rowFields} /> : null}
              <progress className={styles.dropProgress} value={f.progress} max={1} aria-label={progressLabel(f.name)} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A queued file's own fields, on a line of their own under its name; nothing where the caller draws none for it. */
function RowFields({ file, draw }: { file: QueuedFile; draw: (file: QueuedFile) => ReactNode }) {
  const fields = draw(file);
  return fields ? <div className={styles.dropFields} data-upload-row-fields={file.id}>{fields}</div> : null;
}

export interface DropPickProps {
  /** The field's own name, read by a screen reader as the hidden input's label. */
  label: string;
  /** The kinds the browser's picker offers first, as the input's `accept`. */
  accept?: string;
  /** The file picked or dropped; the page decides whether to keep it and when to send it. */
  onPick: (file: File | null) => void;
  /** The name of the file the page holds, shown under the prompt. */
  picked?: string | null;
  disabled?: boolean;
  /** For a suite or a page that reaches for the hidden input by name. */
  dataAttribute?: Record<string, string>;
}

/**
 * The one-file face of the drop zone (decision 1450): the same dashed area,
 * prompt and outlined Choose button, for a field that takes one file and sends
 * it on a later press, as the Organization logo does. It queues nothing and
 * uploads nothing; the page holds the file. 🔒 The browser's own control is
 * hidden and kept, as the drop zone's is (decision 1328).
 */
export function DropPick({ label, accept, onPick, picked = null, disabled = false, dataAttribute }: DropPickProps) {
  const inputId = useId();
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const openFromArea = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled || (e.target as HTMLElement).closest('button, a, input, label')) return;
    input.current?.click();
  };
  return (
    <div
      className={styles.dropZone}
      data-drop-zone="one"
      data-over={over ? 'true' : 'false'}
      onClick={openFromArea}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) onPick(e.dataTransfer.files[0] ?? null);
      }}
    >
      <input
        ref={input}
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        className={styles.dropInput}
        aria-label={label}
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        {...(dataAttribute ?? {})}
      />
      <label htmlFor={inputId} className={styles.dropLabel}>
        <Icon meaning="uploadSlot" size="header" />
        <span>{t('drop.promptOne')}</span>{' '}
        <span className={styles.dropChoose} data-drop-choose="true">
          {t('drop.chooseOne')}
        </span>
      </label>
      {picked ? (
        <p className={styles.dropPicked} data-drop-picked="true">
          {t('drop.picked')} <span className={styles.dropName}>{picked}</span>
        </p>
      ) : null}
    </div>
  );
}
